import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Menu,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  AttachMoney as MoneyIcon,
  Pending as PendingIcon,
  CheckCircle as PaidIcon,
  Error as FailedIcon,
  FilterList as FilterIcon,
  DeleteOutline as WaiveIcon,
  PostAdd as AdhocIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const Payments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [statistics, setStatistics] = useState({
    total_revenue: 0,
    pending_amount: 0,
    paid_count: 0,
    pending_count: 0,
    failed_count: 0,
    payment_by_type: {}
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPayments, setTotalPayments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [batches, setBatches] = useState([]);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    student_id: '',
    amount: '',
    payment_type: 'tuition',
    payment_method: 'upi',
    transaction_id: '',
    description: '',
    status: 'pending'
  });
  const [statusDialog, setStatusDialog] = useState({ open: false, status: '', details: null });

  // Ad-hoc dues
  const [adhocDues, setAdhocDues] = useState([]);
  const [adhocDialog, setAdhocDialog] = useState(false);
  const [adhocForm, setAdhocForm] = useState({
    target: 'student',
    student_id: '',
    batch_id: '',
    amount: '',
    reason: '',
    due_type: 'other',
    due_date: '',
  });
  const [adhocLoading, setAdhocLoading] = useState(false);

  const paymentTypes = [
    { value: 'tuition', label: 'Tuition Fee' },
    { value: 'registration', label: 'Registration Fee' },
    { value: 'event', label: 'Event Fee' },
    { value: 'costume', label: 'Costume Fee' },
    { value: 'other', label: 'Other' }
  ];

  const paymentMethods = [
    { value: 'upi', label: 'UPI' },
    { value: 'net_banking', label: 'Net Banking' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'cash', label: 'Cash' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  // Fetch payments when pagination or filters change
  useEffect(() => {
    fetchPayments();
  }, [page, rowsPerPage, statusFilter, typeFilter, monthFilter, yearFilter, batchFilter]);

  // Fetch students, batches and statistics only once on mount
  useEffect(() => {
    fetchStudents();
    fetchStatistics();
    fetchBatches();
    fetchAdhocDues();
  }, []);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.payment_type = typeFilter;
      if (monthFilter !== 'all') params.for_month = monthFilter;
      if (yearFilter !== 'all') params.for_year = yearFilter;
      if (batchFilter !== 'all') params.batch_id = batchFilter;

      const response = await axios.get('/api/payments/', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setPayments(response.data.payments || []);
      setTotalPayments(response.data.total || 0);
      setTotalPages(response.data.pages || 0);
    } catch (error) {
      showSnackbar('Error fetching payments', 'error');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const handleMonthFilterChange = (event) => {
    setMonthFilter(event.target.value);
    setPage(0);
  };

  const handleYearFilterChange = (event) => {
    setYearFilter(event.target.value);
    setPage(0);
  };

  const handleBatchFilterChange = (event) => {
    setBatchFilter(event.target.value);
    setPage(0);
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/students', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/batches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchAdhocDues = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/dues?status=pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdhocDues(response.data.dues || []);
    } catch (error) {
      console.error('Error fetching ad-hoc dues:', error);
    }
  };

  const handleCreateAdhocDue = async () => {
    if (!adhocForm.amount || !adhocForm.reason.trim()) {
      showSnackbar('Amount and reason are required', 'error');
      return;
    }
    if (adhocForm.target === 'student' && !adhocForm.student_id) {
      showSnackbar('Please select a student', 'error');
      return;
    }
    if (adhocForm.target === 'batch' && !adhocForm.batch_id) {
      showSnackbar('Please select a batch', 'error');
      return;
    }
    setAdhocLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        target: adhocForm.target,
        amount: parseFloat(adhocForm.amount),
        reason: adhocForm.reason,
        due_type: adhocForm.due_type,
        due_date: adhocForm.due_date || null,
      };
      if (adhocForm.target === 'student') payload.student_id = adhocForm.student_id;
      else payload.batch_id = adhocForm.batch_id;

      const res = await axios.post('/api/admin/dues', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar(res.data.message, 'success');
      setAdhocDialog(false);
      setAdhocForm({ target: 'student', student_id: '', batch_id: '', amount: '', reason: '', due_type: 'other', due_date: '' });
      fetchAdhocDues();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating due', 'error');
    } finally {
      setAdhocLoading(false);
    }
  };

  const handleWaiveDue = async (dueId) => {
    if (!window.confirm('Waive this due? The student will no longer be asked to pay it.')) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/admin/dues/${dueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Due waived successfully', 'success');
      fetchAdhocDues();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error waiving due', 'error');
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/payments/statistics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleOpenDialog = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      setFormData({
        student_id: payment.student_id || '',
        amount: payment.amount || '',
        payment_type: payment.payment_type || 'tuition',
        payment_method: payment.payment_method || 'upi',
        transaction_id: payment.transaction_id || '',
        description: payment.description || '',
        status: payment.status || 'pending'
      });
    } else {
      setEditingPayment(null);
      setFormData({
        student_id: '',
        amount: '',
        payment_type: 'tuition',
        payment_method: 'upi',
        transaction_id: '',
        description: '',
        status: 'pending'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPayment(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.student_id || !formData.amount) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      const token = localStorage.getItem('access_token');
      const url = editingPayment
        ? `/api/payments/${editingPayment.id}`
        : '/api/payments/';
      
      const method = editingPayment ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSnackbar(
        editingPayment ? 'Payment updated successfully' : 'Payment added successfully',
        'success'
      );
      
      handleCloseDialog();
      fetchPayments();
      fetchStatistics();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving payment', 'error');
    }
  };

  const handleDeletePayment = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/payments/${selectedPayment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showSnackbar('Payment deleted successfully', 'success');
      handleCloseMenu();
      fetchPayments();
      fetchStatistics();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting payment', 'error');
    }
  };

  const handleMenuOpen = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <PendingIcon fontSize="small" /> },
      paid: { color: 'success', icon: <PaidIcon fontSize="small" /> },
      failed: { color: 'error', icon: <FailedIcon fontSize="small" /> },
      refunded: { color: 'info', icon: <MoneyIcon fontSize="small" /> },
      no_record: { color: 'default', icon: null },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const label = status === 'no_record' ? 'No Record' : status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <Chip
        label={label}
        color={config.color}
        icon={config.icon}
        size="small"
      />
    );
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? student.student_name : 'Unknown';
  };

  const handleUnrecordPayment = async () => {
    if (!selectedPayment) return;
    if (!window.confirm(`Revert "${selectedPayment.student_name || getStudentName(selectedPayment.student_id)}"'s ${selectedPayment.for_month ? `Month ${selectedPayment.for_month}/${selectedPayment.for_year}` : ''} payment to Pending?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(`/api/payments/${selectedPayment.id}/unrecord`, { reason: 'Unrecorded by admin' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar(res.data.message, 'success');
      handleCloseMenu();
      fetchPayments();
      fetchStatistics();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error unrecording payment', 'error');
    }
  };

  const handleCheckStatus = async (payment) => {
    if (!payment.transaction_id) {
      showSnackbar('No transaction ID for this payment', 'warning');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`/api/payments/razorpay-status/${payment.transaction_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusDialog({ open: true, status: res.data.status, details: res.data.payment });
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error fetching status', 'error');
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3 
        }}>
          <Typography variant="h4" fontWeight={700}>
            Payment Management
          </Typography>
          {/* TEMPORARILY HIDDEN - Add Payment button (can be re-enabled later)
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            Add Payment
          </Button>
          */}
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%', ml: 0 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {formatCurrency(statistics.total_revenue)}
                    </Typography>
                    <Typography variant="body2">Total Revenue</Typography>
                  </Box>
                  <PaidIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {formatCurrency(statistics.pending_amount)}
                    </Typography>
                    <Typography variant="body2">Pending Amount</Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {statistics.paid_count}
                    </Typography>
                    <Typography variant="body2">Paid Payments</Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {statistics.pending_count}
                    </Typography>
                    <Typography variant="body2">Pending Payments</Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Payment by Type Breakdown */}
        {Object.keys(statistics.payment_by_type || {}).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Payment Breakdown by Type
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(statistics.payment_by_type).map(([type, data]) => (
                  <Grid item xs={12} sm={6} md={4} key={type}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {type.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {formatCurrency(data.total_amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.count} payment{data.count !== 1 ? 's' : ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Payment Records ({totalPayments} total)
              </Typography>
              
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Month</InputLabel>
                  <Select value={monthFilter} label="Month" onChange={handleMonthFilterChange}>
                    <MenuItem value="all">All Months</MenuItem>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                      <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Year</InputLabel>
                  <Select value={yearFilter} label="Year" onChange={handleYearFilterChange}>
                    <MenuItem value="all">All Years</MenuItem>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Batch</InputLabel>
                  <Select value={batchFilter} label="Batch" onChange={handleBatchFilterChange}>
                    <MenuItem value="all">All Batches</MenuItem>
                    {batches.map(b => (
                      <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="refunded">Refunded</MenuItem>
                    <MenuItem value="no_record">No Record</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    label="Type"
                    onChange={handleTypeFilterChange}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="tuition">Tuition</MenuItem>
                    <MenuItem value="registration">Registration</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="costume">Costume</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            {payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" py={3}>
                No payments found.
              </Typography>
            ) : isMobile ? (
              // Mobile Card View
              <Stack spacing={2} sx={{ mt: 2 }}>
                {payments.map((payment) => (
                  <Card 
                    key={payment.id} 
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(212, 175, 55, 0.2)',
                      boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                        transform: 'translateY(-2px)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                      },
                    }}
                  >
                    <CardContent sx={{ pl: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }}>
                            {payment.student_name || getStudentName(payment.student_id)}
                          </Typography>
                          <Typography variant="h6" fontWeight={700} sx={{ color: '#D4AF37', mt: 0.5 }}>
                            {formatCurrency(payment.total_amount ?? payment.amount)}
                          </Typography>
                          {payment.penalty_amount > 0 && (
                            <Typography variant="caption" color="error.main" display="block">
                              Fee: {formatCurrency(payment.amount)} + ₹{parseFloat(payment.penalty_amount).toLocaleString('en-IN')} penalty
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {getStatusChip(payment.status)}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, payment)}
                            sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                            Type
                          </Typography>
                          <Chip
                            label={payment.payment_type.replace('_', ' ')}
                            size="small"
                            sx={{ mt: 0.5, bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)', fontWeight: 600 }}
                            variant="outlined"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                            Method
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, color: '#2C2416' }}>
                            {payment.payment_method.replace('_', ' ')}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                            Transaction ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, color: '#2C2416' }}>
                            {payment.transaction_id || 'N/A'}
                          </Typography>
                        </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                          Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, color: '#2C2416' }}>
                          {new Date(payment.created_at).toLocaleDateString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>
                    {payment.transaction_id && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SearchIcon />}
                        sx={{ mt: 1, mb: 1, borderColor: '#D4AF37', color: '#D4AF37' }}
                        onClick={() => handleCheckStatus(payment)}
                      >
                        Check Status
                      </Button>
                    )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              // Desktop Table View
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Student Name</strong></TableCell>
                      <TableCell><strong>Parent / Phone</strong></TableCell>
                      <TableCell><strong>Fee</strong></TableCell>
                      <TableCell><strong>Total (incl. penalty)</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell><strong>Transaction ID</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment, idx) => (
                      <TableRow key={payment.id ?? `no-record-${idx}`} hover>
                        <TableCell>{payment.student_name || getStudentName(payment.student_id)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{payment.parent_name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{payment.phone || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(payment.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(payment.total_amount ?? payment.amount)}
                          </Typography>
                          {payment.penalty_amount > 0 && (
                            <Typography variant="caption" color="error.main">
                              incl. ₹{parseFloat(payment.penalty_amount).toLocaleString('en-IN')} penalty
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(payment.payment_type || '').replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {payment.payment_method ? payment.payment_method.replace('_', ' ') : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {payment.transaction_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(payment.status)}</TableCell>
                        <TableCell>
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-IN') : '—'}
                        </TableCell>
                        <TableCell>
                          {payment.id && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, payment)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                              {payment.transaction_id && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<SearchIcon />}
                                  sx={{ ml: 1, borderColor: '#D4AF37', color: '#D4AF37' }}
                                  onClick={() => handleCheckStatus(payment)}
                                >
                                  Check Status
                                </Button>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalPayments}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: '1px solid rgba(224, 224, 224, 1)',
                mt: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: '#2C2416',
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Ad-hoc Dues Section */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Ad-hoc Dues ({adhocDues.length} pending)
              </Typography>
              <Button
                variant="contained"
                startIcon={<AdhocIcon />}
                onClick={() => setAdhocDialog(true)}
                sx={{ bgcolor: '#D4AF37', '&:hover': { bgcolor: '#B8960C' } }}
              >
                Add Ad-hoc Due
              </Button>
            </Box>
            {adhocDues.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" py={2}>
                No pending ad-hoc dues.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Student</strong></TableCell>
                      <TableCell><strong>Batch</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell><strong>Due Date</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adhocDues.map((due) => (
                      <TableRow key={due.id} hover>
                        <TableCell>{due.student_name}</TableCell>
                        <TableCell>{due.batch_name || '—'}</TableCell>
                        <TableCell>
                          <Chip label={due.due_type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={due.description}>
                            {due.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(due.amount)}</strong>
                        </TableCell>
                        <TableCell>
                          {due.due_date ? new Date(due.due_date).toLocaleDateString('en-IN') : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={due.status}
                            size="small"
                            color={due.status === 'paid' ? 'success' : due.status === 'waived' ? 'default' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {due.status === 'pending' && (
                            <IconButton
                              size="small"
                              color="error"
                              title="Waive this due"
                              onClick={() => handleWaiveDue(due.id)}
                            >
                              <WaiveIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add Ad-hoc Due Dialog */}
        <Dialog open={adhocDialog} onClose={() => setAdhocDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Ad-hoc Due</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Target"
                value={adhocForm.target}
                onChange={e => setAdhocForm({ ...adhocForm, target: e.target.value, student_id: '', batch_id: '' })}
                fullWidth
              >
                <MenuItem value="student">Specific Student</MenuItem>
                <MenuItem value="batch">Entire Batch</MenuItem>
              </TextField>

              {adhocForm.target === 'student' ? (
                <TextField
                  select
                  label="Student *"
                  value={adhocForm.student_id}
                  onChange={e => setAdhocForm({ ...adhocForm, student_id: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="">Select Student</MenuItem>
                  {students.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.student_name}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  select
                  label="Batch *"
                  value={adhocForm.batch_id}
                  onChange={e => setAdhocForm({ ...adhocForm, batch_id: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="">Select Batch</MenuItem>
                  {batches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                label="Amount (₹) *"
                type="number"
                value={adhocForm.amount}
                onChange={e => setAdhocForm({ ...adhocForm, amount: e.target.value })}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />

              <TextField
                label="Reason *"
                value={adhocForm.reason}
                onChange={e => setAdhocForm({ ...adhocForm, reason: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., Annual day costume fee, fee restructuring..."
              />

              <TextField
                select
                label="Due Type"
                value={adhocForm.due_type}
                onChange={e => setAdhocForm({ ...adhocForm, due_type: e.target.value })}
                fullWidth
              >
                <MenuItem value="tuition">Tuition</MenuItem>
                <MenuItem value="registration">Registration</MenuItem>
                <MenuItem value="event">Event</MenuItem>
                <MenuItem value="costume">Costume</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>

              <TextField
                label="Due Date (optional)"
                type="date"
                value={adhocForm.due_date}
                onChange={e => setAdhocForm({ ...adhocForm, due_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdhocDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateAdhocDue}
              disabled={adhocLoading}
              sx={{ bgcolor: '#D4AF37', '&:hover': { bgcolor: '#B8960C' } }}
            >
              {adhocLoading ? 'Creating...' : adhocForm.target === 'batch' ? 'Add for All Batch Students' : 'Add Due'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Payment Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingPayment ? 'Edit Payment' : 'Add New Payment'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Student *"
                name="student_id"
                value={formData.student_id}
                onChange={handleInputChange}
                fullWidth
              >
                <MenuItem value="">Select Student</MenuItem>
                {students.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.student_name} - {student.email}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Amount *"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  startAdornment: '₹'
                }}
              />

              <TextField
                select
                label="Payment Type *"
                name="payment_type"
                value={formData.payment_type}
                onChange={handleInputChange}
                fullWidth
              >
                {paymentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Payment Method *"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleInputChange}
                fullWidth
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Transaction ID"
                name="transaction_id"
                value={formData.transaction_id}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., TXN123456789"
              />

              <TextField
                select
                label="Status *"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                fullWidth
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes about this payment..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editingPayment ? 'Update' : 'Add'} Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => {
            handleOpenDialog(selectedPayment);
            handleCloseMenu();
          }}>
            Edit
          </MenuItem>
          {selectedPayment?.status === 'paid' && (
            <MenuItem onClick={handleUnrecordPayment} sx={{ color: 'warning.dark' }}>
              Unrecord Payment (Revert to Pending)
            </MenuItem>
          )}
          <MenuItem onClick={handleDeletePayment} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        </Menu>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Status Dialog */}
        <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, status: '', details: null })}>
          <DialogTitle>Razorpay Payment Status</DialogTitle>
          <DialogContent>
            <Typography>Status: <b>{statusDialog.status}</b></Typography>
            {statusDialog.details && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  Payment ID: {statusDialog.details.id}<br />
                  Order ID: {statusDialog.details.order_id}<br />
                  Method: {statusDialog.details.method}<br />
                  Amount: ₹{(statusDialog.details.amount/100).toFixed(2)}<br />
                  Email: {statusDialog.details.email}<br />
                  Contact: {statusDialog.details.contact}<br />
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialog({ open: false, status: '', details: null })}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default Payments;
